// screens/SessionSummaryScreen.js - Screen 5
import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { useSync } from '../contexts/SyncContext';
import { queuePhoto, requestBackgroundSync } from '../utils/offlineQueue';
import { hasNativeUploader, nativeEnqueuePhoto, nativeIsReliable } from '../utils/nativeBridge';
import { loadUploadSettings, isWithinSyncWindow } from '../utils/syncWindow';
import { shrinkPhoto } from '../utils/imageResize';
import api, { getErrorMessage } from '../utils/api';

function genId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const BASE_STEP_NAMES = [
  'Production Label', 'Full Set Overview', 'Front View', 'Left Side', 'Back View', 'Right Side'
];

export default function SessionSummaryScreen() {
  const { orderId, sessionId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { t } = useLang();
  const { syncNow } = useSync();

  const photos = state?.photos || Array(BASE_STEP_NAMES.length).fill(null);
  // Step labels come from the capture flow (may include extra photos beyond the base 6).
  const stepNames = state?.steps?.map(s => s.name) || BASE_STEP_NAMES;
  const stepCount = stepNames.length;
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleRetake = (stepIndex) => {
    navigate(`/guided-photo/${orderId}/${sessionId}`, { state: { startAt: stepIndex } });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const total = photos.filter(p => p !== null).length;
      let done = 0;
      const deviceId = navigator.userAgent.slice(0, 40);

      // Queue every shot to the local store (fast, on-device). Uploads then run in
      // the BACKGROUND so the photographer can immediately start the next session
      // instead of waiting for all photos to finish uploading.
      // Outside the factory's sync window nothing is handed to the native
      // queue: the installed APK uploads whatever it gets straight away and
      // cannot be paused over the bridge. The web queue holds the photos instead.
      const uploadSettings = loadUploadSettings();
      const windowOpen = isWithinSyncWindow(uploadSettings);
      const nativeReliable = hasNativeUploader() && await nativeIsReliable();
      const useNative = windowOpen && nativeReliable;
      // APK 2.0: web queue uploads, native keeps a copy as safety net.
      const nativeBackup = windowOpen && hasNativeUploader() && !nativeReliable;
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        if (photo === null) continue;
        const item = {
          id: genId(),
          session_id: sessionId,
          order_id: orderId,
          step_name: stepNames[i],
          step_index: i,
          is_na: photo === 'na',
          device_id: deviceId,
          orientation: 'landscape',
          // Shrunk once, here, so every upload path sends the small file.
          blob: photo === 'na' ? null : await shrinkPhoto(photo.file, uploadSettings),
        };
        if (useNative) {
          // Capacitor shell: the native WorkManager queue uploads with the
          // screen off and the app closed. Fall back to the web queue only if
          // the bridge fails, so a shot is never lost.
          try { await nativeEnqueuePhoto(item); } catch { await queuePhoto(item); }
        } else {
          if (nativeBackup) { try { await nativeEnqueuePhoto(item); } catch {} }
          await queuePhoto(item);
        }
        done++;
        setProgress(Math.round((done / total) * 100));
      }

      // Mark the session complete (best-effort) and kick off the background upload.
      try { await api.patch(`/api/sessions/${sessionId}/complete`); } catch {}
      // Drain the queue ONCE: online → foreground sync (fire-and-forget so the
      // user can move on); offline → register an OS background-sync for reconnect.
      // Triggering both drained the queue twice (foreground + service worker),
      // which uploaded every photo twice — uploads are now idempotent too.
      // (Native enqueue schedules its own WorkManager drain; nothing to kick.)
      if (!useNative) {
        if (navigator.onLine) {
          Promise.resolve().then(() => syncNow()).catch(() => {});
        } else {
          requestBackgroundSync();
        }
        // Held by the sync window: the round above sends nothing, so arm the
        // background sync as well; the worker keeps it armed until the window opens.
        if (!windowOpen && navigator.onLine) requestBackgroundSync();
      }

      toast.info(windowOpen
        ? t('summary.background')
        : t('summary.held').replace('{time}', uploadSettings.sync_window_start));
      navigate(`/saved/${orderId}`, { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const capturedCount = photos.filter(p => p !== null).length;
  const naCount = photos.filter(p => p === 'na').length;
  const photoCount = photos.filter(p => p && p !== 'na').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>←</button>
        <h2>{t('summary.title')}</h2>
        <div style={{ width: 52 }} />
      </div>

      <div className="page-content session-summary-content">
        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 24 }}>
          {[
            { label: t('summary.photos'), value: photoCount, color: 'var(--accent-success)' },
            { label: t('summary.na'), value: naCount, color: 'var(--text-muted)' },
            { label: t('summary.missing'), value: stepCount - capturedCount, color: capturedCount < stepCount ? 'var(--accent-warning)' : 'var(--text-muted)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ textAlign: 'center', padding: 14 }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Photo list */}
        <div className="section-title">Photos</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {stepNames.map((step, i) => {
            const photo = photos[i];
            return (
              <div key={i} style={{
                background: 'var(--bg-card)',
                border: `1px solid ${photo ? 'var(--border-subtle)' : 'rgba(245,166,35,0.3)'}`,
                borderRadius: 'var(--radius-md)', padding: '12px 14px',
                display: 'flex', alignItems: 'center', gap: 12
              }}>
                {/* Thumbnail */}
                <div style={{
                  width: 60, height: 45, borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-tertiary)', overflow: 'hidden', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {photo === 'na' ? (
                    <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-muted)' }}>N/A</span>
                  ) : photo ? (
                    <img src={photo.data} alt={step} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: '1.25rem' }}>📷</span>
                  )}
                </div>

                {/* Step info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{step}</div>
                  <div style={{ fontSize: '0.75rem', marginTop: 2, color: photo === 'na' ? 'var(--text-muted)' : photo ? 'var(--accent-success)' : 'var(--accent-warning)', fontWeight: 500 }}>
                    {photo === 'na' ? t('summary.namarked') : photo ? `✓ ${t('summary.captured')}` : `⚠ ${t('summary.notcaptured')}`}
                  </div>
                </div>

                {/* Retake button */}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleRetake(i)}
                  style={{ flexShrink: 0 }}
                >
                  {photo ? `↺ ${t('summary.retake')}` : `📷 ${t('summary.take')}`}
                </button>
              </div>
            );
          })}
        </div>

        {capturedCount < stepCount && (
          <div className="warning-banner" style={{ marginBottom: 16 }}>
            <span>⚠</span>
            <span>{stepCount - capturedCount} step{stepCount - capturedCount > 1 ? 's' : ''} not captured. You can still submit — missing steps will be recorded as incomplete.</span>
          </div>
        )}
      </div>

      {/* Fixed bottom bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-subtle)',
        padding: '12px 16px', display: 'flex', gap: 10
      }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)} style={{ flex: 1 }}>
          ← {t('summary.back')}
        </button>
        <button
          className="btn btn-success"
          onClick={handleSubmit}
          disabled={submitting || capturedCount === 0}
          style={{ flex: 2 }}
        >
          {submitting ? (
            <><div className="spinner" style={{ width: 16, height: 16, borderTopColor: 'var(--text-inverse)' }} /> {t('summary.saving')} {progress}%</>
          ) : (
            `✓ ${t('summary.submit')}`
          )}
        </button>
      </div>
    </div>
  );
}
